import TileLayer from 'ol/layer/Tile.js';
import VectorLayer from 'ol/layer/Vector.js';
import SwisstopoWMTSSource from './layers/swisstopo-wmts.ts';
import {
  ClusteredSource,
  createClusteredStyleFunction,
} from './layers/clustered.ts';
import {createRouteStyle, RouteMVTLayer} from './layers/mvt.ts';
import LayerGroup from 'ol/layer/Group.js';
import {SlowupLayer} from './layers/slowup.ts';
import {SwissparkLayer} from './layers/swisspark.ts';
import {CrosscountryNetworkLayer} from './layers/crosscountry.ts';
import {
  ACCOMMODATION_CODES,
  CLUSTERED_LAYER_CODES,
  OVERLAY_LAYER_CODES,
  ROUTE_LAYERS,
  STATION_CODES,
} from '../layers/layerConstants.ts';
import type {StyleLike} from 'ol/style/Style';
import type {
  LandCode,
  LandLayers,
  LayertreeCode,
  RouteLayerInfo,
} from '../layers/layerTypes.ts';
import {
  apiCodeToLandCode,
  getLowercaseLandCategories,
  isLayertreeCode,
  isMultiLayer,
} from '../layers/layerHelpers.ts';
import {sendAnalytics} from '../analytics.ts';
import {
  isDetoursEnabled,
  isLogosEnabled,
  isPhotosEnabled,
  isShootingEnabled,
} from '../urlHandling/urlSearchParams.ts';
import {disableKvp$, season$} from '../stores/store-main.ts';
import {visibleAdditionalRouteLayers$} from '../stores/store-map.ts';
import {Z_INDEXES} from './constants.ts';
import {getHighlightStyle} from './layers/highlight.ts';
import type VectorTileLayer from 'ol/layer/VectorTile';
import type VectorTileSource from 'ol/source/VectorTile';
import {LogoLayer} from './layers/logo.ts';
import {PhotoLayer} from './layers/photo.ts';
import {DetourLayerGroup} from './layers/detour.ts';
import {getCached} from './cache-helper.ts';
import VectorSource from 'ol/source/Vector.js';
import type {ImageTile, Tile} from 'ol';
import SwisstopoWMS from './layers/swisstopo-wms.ts';

export const WINTER_LAYER_PLACEHOLDER = '[SEASON_DEPENDENT]';

export class LayersManager {
  season = season$.getValue();
  // List of base layers, indexed by their URL parameters name (bgLayer key).
  // Only one layer can be active at a time.
  baseLayers = {
    pk: new TileLayer({
      visible: false,
      opacity: 0.85,
      zIndex: Z_INDEXES.BACKGROUND,
      preload: Infinity,
      source: new SwisstopoWMTSSource(
        WINTER_LAYER_PLACEHOLDER,
        'current',
        'jpeg',
        {
          tileLoadFunction: (imageTile: Tile, src: string) => {
            let layer = 'ch.swisstopo.pixelkarte-farbe';
            if (this.season === 'winter') {
              const z = imageTile.getTileCoord()[0];
              if (z >= 20 && z <= 25) {
                // Z values 20, 21, 22, 23, 24, 25 are resolutions 10, 5, 2.5, 2, 1.5, 1
                layer = 'ch.swisstopo.pixelkarte-farbe-winter';
              }
            }
            const img = (imageTile as ImageTile).getImage() as HTMLImageElement;
            img.src = src.replace(WINTER_LAYER_PLACEHOLDER, layer);
          },
        },
      ),
    }),
    pkg: new TileLayer({
      visible: false,
      opacity: 0.85,
      zIndex: Z_INDEXES.BACKGROUND,
      preload: Infinity,
      source: new SwisstopoWMTSSource(
        'ch.swisstopo.pixelkarte-grau',
        'current',
        'jpeg',
      ),
    }),
    lb: new TileLayer({
      visible: false,
      opacity: 1,
      zIndex: Z_INDEXES.BACKGROUND,
      preload: Infinity,
      source: new SwisstopoWMTSSource(
        'ch.swisstopo.swissimage',
        'current',
        'jpeg',
      ),
    }),
  };
  alpguarddog = new TileLayer({
    visible: false,
    opacity: 0.5,
    zIndex: Z_INDEXES.OVERLAY,
    source: new SwisstopoWMTSSource(
      'ch.bafu.alpweiden-herdenschutzhunde',
      'current',
      'png',
    ),
    properties: {name: 'alpguarddog'},
  });
  shooting = new TileLayer({
    visible: false,
    opacity: 0.5,
    zIndex: Z_INDEXES.OVERLAY,
    source: new SwisstopoWMS('ch.vbs.schiessanzeigen', 'current', 'png'),
    maxResolution: 20,
    properties: {name: 'shooting'},
  });
  slope = new TileLayer({
    visible: false,
    opacity: 0.4,
    zIndex: Z_INDEXES.OVERLAY,
    source: new SwisstopoWMTSSource(
      'ch.swisstopo.hangneigung-ueber_30',
      'current',
      'png',
    ),
    properties: {name: 'slope'},
  });
  wanderwegnetz = new TileLayer({
    visible: false,
    opacity: 0.7,
    maxResolution: 50,
    zIndex: Z_INDEXES.HIKING_TRAILS,
    source: new SwisstopoWMTSSource(
      'ch.swisstopo.swisstlm3d-wanderwege',
      'current',
      'png',
    ),
    properties: {name: 'Wanderwegnetz'},
  });
  wildlife = new LayerGroup({
    visible: false,
    opacity: 0.6,
    zIndex: Z_INDEXES.OVERLAY,
    layers: [
      new TileLayer({
        source: new SwisstopoWMTSSource(
          'ch.bafu.wrz-wildruhezonen_portal',
          'current',
          'png',
        ),
      }),
      new TileLayer({
        source: new SwisstopoWMTSSource(
          'ch.bafu.wrz-jagdbanngebiete_select',
          'current',
          'png',
        ),
      }),
    ],
    properties: {name: 'wildlife'},
  });
  slowup = new SlowupLayer();
  swisspark = new SwissparkLayer();

  municipalities = new TileLayer({
    visible: false,
    zIndex: Z_INDEXES.BACKGROUND,
    source: new SwisstopoWMTSSource(
      'ch.swisstopo.swissboundaries3d-gemeinde-flaeche.fill',
      'current',
      'png',
    ),
    properties: {name: 'municipalities'},
  });
  cantons = new TileLayer({
    visible: false,
    zIndex: Z_INDEXES.BACKGROUND,
    source: new SwisstopoWMTSSource(
      'ch.swisstopo.swissboundaries3d-kanton-flaeche.fill',
      'current',
      'png',
    ),
    properties: {name: 'cantons'},
  });

  crosscountrynetwork = new CrosscountryNetworkLayer({
    visible: false,
    opacity: 0.7,
    maxResolution: 50,
    zIndex: Z_INDEXES.HIKING_TRAILS,
  });

  train: VectorLayer<VectorSource>;
  bus: VectorLayer<VectorSource>;
  trambus: VectorLayer<VectorSource>;
  boat: VectorLayer<VectorSource>;
  cableway: VectorLayer<VectorSource>;
  chairlift: VectorLayer<VectorSource>;
  telecabin: VectorLayer<VectorSource>;
  funicular: VectorLayer<VectorSource>;
  hotel: VectorLayer<VectorSource>;
  bedbreakfast: VectorLayer<VectorSource>;
  youthhostel: VectorLayer<VectorSource>;
  backpacker: VectorLayer<VectorSource>;
  groupaccom: VectorLayer<VectorSource>;
  sleepingstraw: VectorLayer<VectorSource>;
  farmaccom: VectorLayer<VectorSource>;
  vacationapptmt: VectorLayer<VectorSource>;
  mountainhut: VectorLayer<VectorSource>;
  campingsite: VectorLayer<VectorSource>;
  place: VectorLayer<VectorSource>;
  sightseeing: VectorLayer<VectorSource>;
  velorental: VectorLayer<VectorSource>;
  cycleservice: VectorLayer<VectorSource>;

  highlightLayer = new VectorLayer({
    visible: true,
    zIndex: Z_INDEXES.HIGHLIGHT,
    source: getCached('highlight_source', () => new VectorSource()),
    style: getHighlightStyle(),
    properties: {name: 'highlight'},
  });

  landLayers: Record<LandLayers, VectorTileLayer<VectorTileSource>> = {
    crosscountry: undefined,
    kanuland: undefined,
    mtbland: undefined,
    skatingland: undefined,
    sledging: undefined,
    snowshoe: undefined,
    veloland: undefined,
    wanderland: undefined,
    winterhiking: undefined,
  };
  logoLayers: Record<LandLayers, LogoLayer> = {
    crosscountry: undefined,
    kanuland: undefined,
    mtbland: undefined,
    skatingland: undefined,
    sledging: undefined,
    snowshoe: undefined,
    veloland: undefined,
    wanderland: undefined,
    winterhiking: undefined,
  };
  detourLayers: Record<LandLayers, DetourLayerGroup> = {
    crosscountry: undefined,
    kanuland: undefined,
    mtbland: undefined,
    skatingland: undefined,
    sledging: undefined,
    snowshoe: undefined,
    veloland: undefined,
    wanderland: undefined,
    winterhiking: undefined,
  };
  photoLayers: Record<LandLayers, PhotoLayer> = {
    crosscountry: undefined,
    kanuland: undefined,
    mtbland: undefined,
    skatingland: undefined,
    sledging: undefined,
    snowshoe: undefined,
    veloland: undefined,
    wanderland: undefined,
    winterhiking: undefined,
  };
  WMTSClickableLayers;

  constructor() {
    Object.values(ROUTE_LAYERS).forEach((layerInfo: RouteLayerInfo) => {
      const key = layerInfo.layer;
      this.landLayers[key] = new RouteMVTLayer(layerInfo.legacyLandCode, key);
      this.logoLayers[key] = new LogoLayer(layerInfo.logoFilename, key);
      this.photoLayers[key] = new PhotoLayer(layerInfo.legacyLandCode, key);
      this.detourLayers[key] = new DetourLayerGroup(
        layerInfo.legacyLandCode,
        key,
      );
    });

    this.WMTSClickableLayers = [
      ...this.wildlife.getLayersArray(),
      this.alpguarddog,
      this.municipalities,
      this.cantons,
      this.shooting,
    ];

    CLUSTERED_LAYER_CODES.forEach((layer) => {
      this[layer] = new VectorLayer<VectorSource>({
        visible: false,
        zIndex: Z_INDEXES.POINT,
        style: getCached(`${layer}_style`, () =>
          createClusteredStyleFunction(layer),
        ),
        source: getCached(`${layer}_source`, () => new ClusteredSource(layer)),
        properties: {name: layer},
      });
    });

    season$.subscribe((newSeason) => {
      this.season = newSeason;
      this.baseLayers.pk.getSource().refresh();
    });
  }

  activateBaseLayer(code: string) {
    Object.entries(this.baseLayers).forEach(([key, layer]) =>
      layer.setVisible(key === code),
    );
  }

  activateLayers(previousCodes: LayertreeCode[], codes: LayertreeCode[]) {
    const freshlyActivatedCodes =
      codes && previousCodes
        ? codes.filter((c) => !previousCodes.includes(c))
        : codes;
    const additionalRouteLayers = visibleAdditionalRouteLayers$.getValue();
    const disableKvpValue = disableKvp$.getValue();
    let atLeastOneLandVisible = false;
    Object.entries(this.landLayers).forEach(([key, value]) => {
      const landLayer = <LandLayers>key;
      const groupSelected = codes.includes(landLayer);
      const selection: string[] = [];
      if (isMultiLayer(landLayer)) {
        const categories = getLowercaseLandCategories(landLayer);
        if (groupSelected) {
          selection.push(...categories);
        } else {
          categories.forEach((c: LayertreeCode) => {
            if (codes.includes(c)) selection.push(c);
          });
        }
      } else if (groupSelected) {
        selection.push(landLayer);
      }

      const visible = !!codes.find(
        (c) =>
          c.includes(landLayer) ||
          (landLayer === 'wanderland' && c.includes('wanderwegnetz')) ||
          (landLayer === 'crosscountry' && c.includes('crosscountrynetwork')),
      );
      if (visible && !atLeastOneLandVisible) {
        atLeastOneLandVisible = true;
      }
      value.setStyle(<StyleLike>createRouteStyle(selection));
      value.setVisible(visible);

      const freshlyVisible = !!freshlyActivatedCodes.find(
        (c) =>
          c.includes(landLayer) ||
          (landLayer === 'wanderland' && c.includes('wanderwegnetz')) ||
          (landLayer === 'crosscountry' && c.includes('crosscountrynetwork')),
      );
      if (freshlyVisible) {
        if (groupSelected) {
          sendAnalytics('event', 'layers', {event_label: landLayer});
        } else {
          selection.forEach((key) => {
            if (freshlyActivatedCodes.includes(<LandLayers>key)) {
              sendAnalytics('event', 'layers', {event_label: key});
            }
          });
        }
      }

      const landCode: LandCode = apiCodeToLandCode[landLayer];
      if (landCode in ROUTE_LAYERS) {
        const detourVisible = disableKvpValue
          ? additionalRouteLayers.detours
          : isDetoursEnabled();
        this.detourLayers[landLayer].setVisible(visible && detourVisible);

        const logoVisible = disableKvpValue
          ? additionalRouteLayers.logos
          : isLogosEnabled();
        this.logoLayers[landLayer].updateStyles(selection);
        this.logoLayers[landLayer].setVisible(visible && logoVisible);

        const photoVisible = disableKvpValue
          ? additionalRouteLayers.photos
          : isPhotosEnabled();
        this.photoLayers[landLayer].setVisible(visible && photoVisible);
      }
    });

    const shootingVisible = disableKvpValue
      ? additionalRouteLayers.shooting
      : isShootingEnabled();
    this.shooting.setVisible(atLeastOneLandVisible && shootingVisible);

    OVERLAY_LAYER_CODES.forEach((code) => {
      const layertreeCode = isLayertreeCode(code) ? code : undefined;
      const visible =
        codes.includes(layertreeCode) ||
        (layertreeCode === 'wanderwegnetz' &&
          (codes.includes('wanderland') || codes.includes('wanderwegnetz'))) ||
        (layertreeCode === 'crosscountrynetwork' &&
          (codes.includes('crosscountry') ||
            codes.includes('crosscountrynetwork')));
      this[code].setVisible(visible);
      const freshlyVisible = freshlyActivatedCodes.includes(code);
      if (freshlyVisible) {
        if (code === 'slowup') {
          sendAnalytics('event', 'slowup');
        } else {
          sendAnalytics('event', 'layers', {event_label: code});
        }
      }
    });
    if (codes.includes('accommodation')) {
      ACCOMMODATION_CODES.forEach((layer) => this[layer].setVisible(true));
    }
    if (freshlyActivatedCodes.includes('accommodation')) {
      sendAnalytics('event', 'layers', {event_label: 'Accommodation'});
    }
    if (codes.includes('station')) {
      STATION_CODES.forEach((layer) => this[layer].setVisible(true));
    }
    if (freshlyActivatedCodes.includes('station')) {
      sendAnalytics('event', 'layers', {event_label: 'Station'});
    }
  }
}
